// SPDX-License-Identifier: AGPL-3.0-or-later

import {ChangeFriendNicknameModal} from '@app/features/relationship/components/modals/ChangeFriendNicknameModal';
import Relationships from '@app/features/relationship/state/Relationships';
import * as RelationshipActionUtils from '@app/features/relationship/utils/RelationshipActionUtils';
import {
	AcceptFriendRequestIcon,
	BlockUserIcon,
	CancelFriendRequestIcon,
	ChangeNicknameIcon,
	IgnoreFriendRequestIcon,
	RemoveFriendIcon,
	SendFriendRequestIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {PublicUserFlags, RelationshipTypes} from '@app/vendor/constants/UserConstants';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useState} from 'react';

interface SendFriendRequestMenuItemProps {
	user: User;
	onClose: () => void;
}
export const SendFriendRequestMenuItem: React.FC<SendFriendRequestMenuItemProps> = observer(({user, onClose}) => {
	const {t, i18n} = useLingui();
	const relationshipType = Relationships.getRelationship(user.id)?.type;
	const [submitting, setSubmitting] = useState(false);
	const showFriendRequestSent = relationshipType === RelationshipTypes.OUTGOING_REQUEST;
	const isCurrentUserStaff = Users.currentUser?.isStaff() ?? false;
	const isCurrentUserUnclaimed = !(Users.currentUser?.isClaimed() ?? true);
	const isCurrentUserUnverified = Users.currentUser?.verified === false;
	const handleSendFriendRequest = useCallback(async () => {
		if (submitting || showFriendRequestSent) return;
		if (isCurrentUserStaff) {
			onClose();
			RelationshipActionUtils.showSendFriendRequestConfirmation(i18n, user);
			return;
		}
		setSubmitting(true);
		try {
			await RelationshipActionUtils.sendFriendRequest(i18n, user.id);
		} finally {
			setSubmitting(false);
		}
	}, [i18n, isCurrentUserStaff, onClose, showFriendRequestSent, submitting, user]);
	if (isCurrentUserUnclaimed || isCurrentUserUnverified) {
		const tooltip = isCurrentUserUnclaimed
			? t`Claim your account to send friend requests.`
			: t`Verify your email address to send friend requests.`;
		return (
			<Tooltip text={tooltip} maxWidth="xl">
				<div>
					<MenuItem
						icon={<SendFriendRequestIcon />}
						onClick={handleSendFriendRequest}
						disabled={true}
						closeOnSelect={false}
					>
						{showFriendRequestSent ? t`Friend Request Sent` : t`Add Friend`}
					</MenuItem>
				</div>
			</Tooltip>
		);
	}
	return (
		<MenuItem
			icon={<SendFriendRequestIcon />}
			onClick={handleSendFriendRequest}
			disabled={submitting || showFriendRequestSent}
			closeOnSelect={false}
		>
			{showFriendRequestSent ? t`Friend Request Sent` : t`Add Friend`}
		</MenuItem>
	);
});
interface AcceptFriendRequestMenuItemProps {
	user: User;
	onClose: () => void;
}
export const AcceptFriendRequestMenuItem: React.FC<AcceptFriendRequestMenuItemProps> = observer(({user, onClose}) => {
	const {t, i18n} = useLingui();
	const handleAcceptFriendRequest = useCallback(
		(event?: {shiftKey?: boolean}) => {
			onClose();
			RelationshipActionUtils.showAcceptFriendRequestConfirmation(i18n, user, {
				bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
			});
		},
		[i18n, onClose, user],
	);
	return (
		<MenuItem icon={<AcceptFriendRequestIcon />} onClick={handleAcceptFriendRequest}>
			{t`Accept Friend Request`}
		</MenuItem>
	);
});
interface RemoveFriendMenuItemProps {
	user: User;
	onClose: () => void;
	danger?: boolean;
}
export const RemoveFriendMenuItem: React.FC<RemoveFriendMenuItemProps> = observer(({user, onClose, danger = true}) => {
	const {t, i18n} = useLingui();
	const handleRemoveFriend = useCallback(
		(event?: {shiftKey?: boolean}) => {
			onClose();
			RelationshipActionUtils.showRemoveFriendConfirmation(i18n, user, {
				bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
			});
		},
		[i18n, user, onClose],
	);
	return (
		<MenuItem icon={<RemoveFriendIcon />} onClick={handleRemoveFriend} danger={danger}>
			{t`Remove Friend`}
		</MenuItem>
	);
});
interface ChangeFriendNicknameMenuItemProps {
	user: User;
	onClose: () => void;
}
export const ChangeFriendNicknameMenuItem: React.FC<ChangeFriendNicknameMenuItemProps> = observer(({user, onClose}) => {
	const {t} = useLingui();
	const relationship = Relationships.getRelationship(user.id);
	const handleChangeNickname = useCallback(() => {
		onClose();
		ModalCommands.push(modal(() => <ChangeFriendNicknameModal user={user} />));
	}, [onClose, user]);
	if (relationship?.type !== RelationshipTypes.FRIEND) {
		return null;
	}
	return (
		<MenuItem icon={<ChangeNicknameIcon />} onClick={handleChangeNickname}>
			{t`Change Friend Nickname`}
		</MenuItem>
	);
});
interface IgnoreFriendRequestMenuItemProps {
	user: User;
	onClose: () => void;
}
export const IgnoreFriendRequestMenuItem: React.FC<IgnoreFriendRequestMenuItemProps> = observer(({user, onClose}) => {
	const {t, i18n} = useLingui();
	const handleIgnoreFriendRequest = useCallback(() => {
		onClose();
		RelationshipActionUtils.ignoreFriendRequest(i18n, user.id);
	}, [i18n, user.id, onClose]);
	return (
		<MenuItem icon={<IgnoreFriendRequestIcon />} onClick={handleIgnoreFriendRequest}>
			{t`Ignore Friend Request`}
		</MenuItem>
	);
});
interface CancelFriendRequestMenuItemProps {
	user: User;
	onClose: () => void;
}
export const CancelFriendRequestMenuItem: React.FC<CancelFriendRequestMenuItemProps> = observer(({user, onClose}) => {
	const {t, i18n} = useLingui();
	const handleCancelFriendRequest = useCallback(() => {
		onClose();
		RelationshipActionUtils.cancelFriendRequest(i18n, user.id);
	}, [i18n, user.id, onClose]);
	return (
		<MenuItem icon={<CancelFriendRequestIcon />} onClick={handleCancelFriendRequest}>
			{t`Cancel Friend Request`}
		</MenuItem>
	);
});
interface BlockUserMenuItemProps {
	user: User;
	onClose: () => void;
}
export const BlockUserMenuItem: React.FC<BlockUserMenuItemProps> = observer(({user, onClose}) => {
	const {t, i18n} = useLingui();
	const handleBlockUser = useCallback(
		(event?: {shiftKey?: boolean}) => {
			onClose();
			RelationshipActionUtils.showBlockUserConfirmation(i18n, user, {
				bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
			});
		},
		[i18n, user, onClose],
	);
	if (user.system) {
		return null;
	}
	return (
		<MenuItem icon={<BlockUserIcon />} onClick={handleBlockUser} danger>
			{t`Block`}
		</MenuItem>
	);
});
interface UnblockUserMenuItemProps {
	user: User;
	onClose: () => void;
}
export const UnblockUserMenuItem: React.FC<UnblockUserMenuItemProps> = observer(({user, onClose}) => {
	const {t, i18n} = useLingui();
	const handleUnblockUser = useCallback(
		(event?: {shiftKey?: boolean}) => {
			onClose();
			RelationshipActionUtils.showUnblockUserConfirmation(i18n, user, {
				bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
			});
		},
		[i18n, user, onClose],
	);
	if (user.system) {
		return null;
	}
	return (
		<MenuItem icon={<BlockUserIcon />} onClick={handleUnblockUser}>
			{t`Unblock`}
		</MenuItem>
	);
});
interface RelationshipActionMenuItemProps {
	user: User;
	onClose: () => void;
}
export const RelationshipActionMenuItem: React.FC<RelationshipActionMenuItemProps> = observer(({user, onClose}) => {
	const {t} = useLingui();
	const relationship = Relationships.getRelationship(user.id);
	const relationshipType = relationship?.type;
	const isFriendlyBot = user.bot && (user.flags & PublicUserFlags.FRIENDLY_BOT) === PublicUserFlags.FRIENDLY_BOT;
	if (user.bot && !isFriendlyBot) {
		if (relationshipType === RelationshipTypes.FRIEND) {
			return <RemoveFriendMenuItem user={user} onClose={onClose} danger={false} />;
		}
		if (relationshipType === RelationshipTypes.INCOMING_REQUEST) {
			return <IgnoreFriendRequestMenuItem user={user} onClose={onClose} />;
		}
		if (relationshipType === RelationshipTypes.OUTGOING_REQUEST) {
			return <CancelFriendRequestMenuItem user={user} onClose={onClose} />;
		}
		return null;
	}
	switch (relationshipType) {
		case RelationshipTypes.FRIEND:
			return <RemoveFriendMenuItem user={user} onClose={onClose} danger={false} />;
		case RelationshipTypes.INCOMING_REQUEST:
			return (
				<>
					<AcceptFriendRequestMenuItem user={user} onClose={onClose} />
					<IgnoreFriendRequestMenuItem user={user} onClose={onClose} />
				</>
			);
		case RelationshipTypes.OUTGOING_REQUEST:
			return (
				<MenuItem icon={<SendFriendRequestIcon />} disabled closeOnSelect={false}>
					{t`Friend Request Sent`}
				</MenuItem>
			);
		case RelationshipTypes.BLOCKED:
			return null;
		default:
			return <SendFriendRequestMenuItem user={user} onClose={onClose} />;
	}
});
